// components/EditUserModal.jsx
import React, { useState, useEffect } from 'react';
import { paises } from "../../../../../components/data/Countries";

const EditUserModal = ({ 
  userToEdit, 
  setUserToEdit, 
  handleEditSubmit, 
  isLoading, 
  updateError, 
  roles,
  selectedRolId,
  setSelectedRolId,
  onClose 
}) => {
  // Estado para rastrear si ha habido cambios
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    // Cuando se cargan los roles o cambia el userToEdit, intentamos encontrar 
    // y establecer el rol correspondiente
    if (roles.length > 0 && userToEdit && userToEdit.rol) {
      const userRole = roles.find(r => r.name === userToEdit.rol);
      if (userRole) {
        setSelectedRolId(userRole.id);
      }
    }
  }, [roles, userToEdit, setSelectedRolId]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setUserToEdit(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    setHasChanges(true);
  };

  const handleRolChange = (e) => {
    setSelectedRolId(e.target.value);
    setHasChanges(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Incluimos un marcador en el userToEdit para forzar la actualización incluso si solo cambia el rol
    const updatedUser = {
      ...userToEdit,
      _forceUpdate: hasChanges || selectedRolId !== roles.find(r => r.name === userToEdit.rol)?.id
    };
    handleEditSubmit(e, updatedUser, selectedRolId);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
      <div className="bg-zinc-800 rounded-lg shadow-xl max-w-2xl w-full p-6 my-8">
        <h3 className="text-xl font-bold text-white mb-6">Editar Usuario</h3>
        
        {updateError && (
          <div className="bg-red-500/20 border border-red-500 text-red-100 px-4 py-3 rounded mb-4">
            <p className="text-sm">{updateError}</p>
          </div>
        )}
        
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Nombre
              </label>
              <input
                type="text"
                name="firstName"
                value={userToEdit.firstName || ''}
                onChange={handleChange}
                className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Apellido
              </label>
              <input
                type="text"
                name="lastName"
                value={userToEdit.lastName || ''}
                onChange={handleChange}
                className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Email
              </label>
              <input
                type="email"
                name="email"
                value={userToEdit.email || ''}
                onChange={handleChange}
                className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Teléfono
              </label>
              <input
                type="text"
                name="phoneNumber"
                value={userToEdit.phoneNumber || ''}
                onChange={handleChange}
                className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-red-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Fecha de Nacimiento
              </label>
              <input
                type="date"
                name="dateOfBirth"
                value={userToEdit.dateOfBirth || ''}
                onChange={handleChange}
                className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-red-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                País
              </label>
              <select
                name="country"
                value={userToEdit.country || ''}
                onChange={handleChange}
                className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-red-500 appearance-none cursor-pointer"
              >
                <option value="" disabled>Seleccione un país</option>
                {paises.map((pais, index) => (
                  <option key={index} value={pais}>
                    {pais}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Rol de Usuario
              </label>
              <select
                value={selectedRolId}
                onChange={handleRolChange}
                className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-red-500 appearance-none cursor-pointer"
                required
              >
                <option value="" disabled>Seleccione un rol</option>
                {roles.map((rol) => (
                  <option key={rol.id} value={rol.id}>
                    {rol.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          
          <div className="flex flex-col md:flex-row space-y-4 md:space-y-0 md:space-x-6 mb-6">
            <div className="flex items-center">
              <input
                type="checkbox"
                name="isPremium"
                checked={userToEdit.isPremium || false}
                onChange={handleChange}
                className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-600 rounded"
              />
              <label className="ml-2 block text-sm text-gray-300">
                Usuario Premium
              </label>
            </div>
            
            <div className="flex items-center">
              <input
                type="checkbox"
                name="habilitado"
                checked={userToEdit.habilitado || false}
                onChange={handleChange}
                className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-600 rounded"
              />
              <label className="ml-2 block text-sm text-gray-300">
                Usuario Habilitado
              </label>
            </div>
          </div>
          
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-zinc-700 text-white rounded hover:bg-zinc-600"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 flex items-center"
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Guardando...
                </>
              ) : (
                'Guardar Cambios'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditUserModal;